import StageHero from './StageHero.vue';
import { ForegroundColor } from 'ewi-ui-helpers/types';
import type { Meta, StoryObj } from '@storybook/vue3';

const meta = {
  title: 'Organisms/Stage Hero',
  component: StageHero,
  render: (args, { globals }) => ({
    components: { StageHero },
    setup() {
      return { args, globals };
    },
    template:
      '<div style="background:#dedede;padding:20px 0;"><div class="container-edge"><StageHero v-bind="args" /></div></div>',
  }),
  tags: ['autodocs'],
  argTypes: {
    heading_1: {
      control: 'text',
      description: 'first heading - leave empty if not needed',
    },
    heading_2: {
      control: 'text',
      description: 'second heading - leave empty if not needed',
    },
    heading_3: {
      control: 'text',
      description: 'third heading - leave empty if not needed',
    },
    textContainerWidth: {
      control: { type: 'select' },
      options: ['narrow', 'medium', 'wide'],
    },
    textContainerPosition: {
      control: { type: 'select' },
      options: ['left', 'center', 'right'],
    },
    textHorizontalAlignment: {
      control: { type: 'select' },
      description: 'Horizontal text alignment',
      options: ['left', 'center', 'right'],
    },
    textVerticalAlignment: {
      control: { type: 'select' },
      description: 'Vertical text alignment',
      options: ['top', 'center', 'bottom'],
    },
    mobileTextContainerWidth: {
      control: { type: 'select' },
      options: ['narrow', 'medium', 'wide'],
    },
    mobileTextContainerPosition: {
      control: { type: 'select' },
      options: ['left', 'center', 'right'],
    },
    mobileTextHorizontalAlignment: {
      control: { type: 'select' },
      description: 'Horizontal text alignment',
      options: ['left', 'center', 'right'],
    },
    mobileTextVerticalAlignment: {
      control: { type: 'select' },
      description: 'Vertical text alignment',
      options: ['top', 'center', 'bottom'],
    },

    textColor: {
      control: { type: 'select' },
      description: 'Text color',
      options: Object.values(ForegroundColor),
    },
    buttonElements: {
      control: 'object',
      description: 'Array of button configurations',
    },
    awardsElement: {
      control: { type: 'boolean' },
      description: 'Enable awards image.',
    },
    hasOverlay: {
      control: { type: 'boolean' },
      description: 'Enable overlay.',
    },
    clipLeftMax: {
      control: { type: 'boolean' },
      description: 'Clip left max',
    },
    clipLeft: {
      control: { type: 'boolean' },
      description: 'Clip left',
    },
    pushLeft: {
      control: { type: 'boolean' },
      description: 'Push text to the left',
    },
  },
  args: {},
} satisfies Meta<typeof StageHero>;

type Story = StoryObj<typeof meta>;

export default meta;

export const Default: Story = {
  args: {
    ...meta.args,
    heading_1: 'SEIT 15 JAHREN:',
    heading_2: 'E WIE ENERGIE',
    heading_3:
      '<p>Unser Service und unsere Strom- und Gas-Tarifewerden regelmäßig ausgezeichnet. Darauf sind wir natürlich besonders stolz!</p><ul><li><strong>great prices</strong></li><li><strong>excellent services</strong></li></ul>',
    textContainerWidth: 'narrow',
    textContainerPosition: 'left',
    textHorizontalAlignment: 'left',
    textVerticalAlignment: 'center',
    mobileTextContainerWidth: 'wide',
    mobileTextContainerPosition: 'left',
    mobileTextHorizontalAlignment: 'right',
    mobileTextVerticalAlignment: 'top',
    textColor: 'white',
    imageConfiguration: {
      imageCaptionPosition: 'left',
      imageRoundedCorners: true,
      defaultSrc:
        '/layout/storybook/sb-assets/Frau_mit_Handy_und_Kopfhoerern_mirrored-0482a50d.jpg',
    },
    buttonElements: [],
    awardsElement: true,
    awardImages: [
      { src: '/layout/storybook/sb-assets/siegel_1.svg?url', alt: '' },
      { src: '/layout/storybook/sb-assets/siegel_2.svg?url', alt: '' },
      { src: '/layout/storybook/sb-assets/siegel_3.svg?url', alt: '' },
      { src: '/layout/storybook/sb-assets/siegel_4.svg?url', alt: '' },
    ],
    hasOverlay: false,
    clipLeftMax: false,
    clipLeft: false,
    pushLeft: false,
  },
};

export const TwoButtons: Story = {
  args: {
    ...meta.args,
    heading_1: 'Welcome to StageHero',
    heading_2: 'With Two Buttons',
    heading_3:
      '<p>Experience the flexibility of having multiple actions available at your fingertips.</p>',
    textContainerWidth: 'medium',
    textContainerPosition: 'center',
    textHorizontalAlignment: 'center',
    textVerticalAlignment: 'center',
    mobileTextContainerWidth: 'wide',
    mobileTextContainerPosition: 'center',
    mobileTextHorizontalAlignment: 'center',
    mobileTextVerticalAlignment: 'center',
    textColor: 'black',
    imageConfiguration: {
      imageCaptionPosition: 'center',
      imageRoundedCorners: true,
      defaultSrc:
        '/layout/storybook/sb-assets/Frau_mit_Handy_und_Kopfhoerern_mirrored-0482a50d.jpg',
    },
    buttonElements: [
      {
        buttonConfiguration: 'filled',
        buttonContent: 'Primary Action',
        buttonUrl: 'https://example.com/primary',
      },
      {
        buttonConfiguration: 'filled',
        buttonContent: 'Secondary Action',
        buttonUrl: 'https://example.com/secondary',
      },
    ],
    awardsElement: false,
    hasOverlay: false,
    clipLeftMax: false,
    clipLeft: false,
    pushLeft: false,
  },
};

export const LongWordHeading: Story = {
  name: 'Long Word Heading (mobile font-size fix)',
  args: {
    ...meta.args,
    heading_1: '',
    heading_2: 'Stromverbrauch beim PC',
    heading_3:
      '<p>Finden Sie jetzt den passenden Tarif für Ihren Haushalt.</p>',
    textContainerWidth: 'narrow',
    textContainerPosition: 'left',
    textHorizontalAlignment: 'left',
    textVerticalAlignment: 'center',
    mobileTextContainerWidth: 'wide',
    mobileTextContainerPosition: 'left',
    mobileTextHorizontalAlignment: 'left',
    mobileTextVerticalAlignment: 'center',
    textColor: 'white',
    imageConfiguration: {
      imageCaptionPosition: 'left',
      imageRoundedCorners: true,
      defaultSrc:
        '/layout/storybook/sb-assets/Frau_mit_Handy_und_Kopfhoerern_mirrored-0482a50d.jpg',
    },
    buttonElements: [],
    awardsElement: false,
    hasOverlay: false,
    clipLeftMax: false,
    clipLeft: false,
    pushLeft: false,
  },
};

export const WithAwardsAndCTA: Story = {
  args: {
    ...meta.args,
    heading_1: 'MEHRFACH AUSGEZEICHNET:',
    heading_2: 'E WIE EINFACH',
    heading_3:
      '<p>Unsere Tarife und unser Service werden regelmäßig mit Top-Bewertungen ausgezeichnet.</p>',
    textContainerWidth: 'narrow',
    textContainerPosition: 'left',
    textHorizontalAlignment: 'left',
    textVerticalAlignment: 'center',
    mobileTextContainerWidth: 'wide',
    mobileTextContainerPosition: 'left',
    mobileTextHorizontalAlignment: 'left',
    mobileTextVerticalAlignment: 'bottom',
    textColor: 'white',
    imageConfiguration: {
      imageCaptionPosition: 'left',
      imageRoundedCorners: true,
      defaultSrc:
        '/layout/storybook/sb-assets/Frau_mit_Handy_und_Kopfhoerern_mirrored-0482a50d.jpg',
    },
    buttonElements: [
      {
        buttonConfiguration: 'filled',
        buttonContent: 'Jetzt Tarif wechseln',
        buttonUrl: 'https://example.com/tarife',
      },
    ],
    awardsElement: true,
    awardImages: [
      { src: '/layout/storybook/sb-assets/siegel_1.svg?url', alt: '' },
      { src: '/layout/storybook/sb-assets/siegel_2.svg?url', alt: '' },
      { src: '/layout/storybook/sb-assets/siegel_3.svg?url', alt: '' },
      { src: '/layout/storybook/sb-assets/siegel_4.svg?url', alt: '' },
    ],
    hasOverlay: false,
    clipLeftMax: false,
    clipLeft: false,
    pushLeft: false,
  },
};
